<template>
  <div class="keys">
    <h1 class="page-title">API Keys</h1>
    <p class="page-desc">
      密钥保存在主站用户表 <code class="mono">users.opengate_api_keys</code>，登录后创建与管理。
    </p>

    <section class="panel create">
      <div class="field">
        <label>密钥名称</label>
        <input
          v-model="name"
          type="text"
          maxlength="40"
          placeholder="例如：生产环境 / 测试"
          :disabled="loading"
          @keyup.enter="onCreate"
        />
      </div>
      <button type="button" class="btn btn-primary" :disabled="loading" @click="onCreate">
        {{ loading ? '处理中…' : '创建密钥' }}
      </button>
    </section>

    <p v-if="flash" class="flash" :class="{ 'is-err': flashErr }">{{ flash }}</p>

    <div v-if="freshSecret" class="panel fresh">
      <div class="fresh__title">新密钥已创建 — 请立即复制 Secret（仅展示一次）</div>
      <div class="fresh__row">
        <span>Key</span>
        <code class="mono">{{ freshSecret.key }}</code>
        <button type="button" class="btn btn-ghost" @click="copy(freshSecret.key)">复制</button>
      </div>
      <div class="fresh__row">
        <span>Secret</span>
        <code class="mono">{{ freshSecret.secret }}</code>
        <button type="button" class="btn btn-ghost" @click="copy(freshSecret.secret)">复制</button>
      </div>
      <button type="button" class="btn btn-primary" @click="freshSecret = null">我已保存</button>
    </div>

    <div v-if="loadingList" class="empty panel">加载中…</div>
    <div v-else-if="!keys.length" class="empty panel">还没有密钥，点击上方「创建密钥」开始。</div>

    <ul v-else class="list">
      <li v-for="k in keys" :key="k.id" class="panel item">
        <div class="item__top">
          <div>
            <div class="item__name">{{ k.name }}</div>
            <div class="item__meta mono">
              创建于 {{ formatDate(k.created_at) }}
              <span :class="k.enabled ? 'on' : 'off'">{{ k.enabled ? '启用' : '已吊销' }}</span>
            </div>
          </div>
          <div class="item__actions">
            <button type="button" class="btn btn-ghost" @click="copy(k.key)">复制 Key</button>
            <button
              v-if="k.enabled"
              type="button"
              class="btn btn-danger"
              :disabled="loading"
              @click="onRevoke(k.id)"
            >
              吊销
            </button>
            <button type="button" class="btn btn-ghost" :disabled="loading" @click="onDelete(k.id)">
              删除
            </button>
          </div>
        </div>
        <div class="item__secrets">
          <div>
            <span>Key</span>
            <code class="mono">{{ k.key }}</code>
          </div>
          <div>
            <span>Secret</span>
            <code class="mono">{{ k.secret_masked || maskSecret('') }}</code>
          </div>
        </div>
      </li>
    </ul>

    <p v-if="maxHint" class="hint mono">{{ maxHint }}</p>
  </div>
</template>

<script>
import {
  fetchApiKeys,
  createApiKey,
  revokeApiKey,
  deleteApiKey,
  pickApiPayload,
  maskSecret
} from '../utils/apiKeysApi'
import { showErrorToast, showSuccessToast } from '../utils/toast'

export default {
  name: 'Keys',
  data() {
    return {
      name: '',
      keys: [],
      max: 20,
      loading: false,
      loadingList: true,
      flash: '',
      flashErr: false,
      freshSecret: null
    }
  },
  computed: {
    maxHint() {
      if (!this.max) return ''
      return `已创建 ${this.keys.length} / ${this.max}`
    }
  },
  created() {
    this.reload()
  },
  methods: {
    maskSecret,
    formatDate(iso) {
      if (!iso) return '—'
      try {
        return new Date(iso).toLocaleString()
      } catch (e) {
        return iso
      }
    },
    setFlash(msg, isErr) {
      this.flash = msg
      this.flashErr = !!isErr
      clearTimeout(this._flashTimer)
      this._flashTimer = setTimeout(() => {
        this.flash = ''
      }, 2800)
    },
    pickErr(e, fallback) {
      return (
        (e && e.response && e.response.data && (e.response.data.message || e.response.data.error)) ||
        (e && e.message) ||
        fallback
      )
    },
    async reload() {
      this.loadingList = true
      try {
        const res = await fetchApiKeys()
        const data = pickApiPayload(res) || {}
        this.keys = Array.isArray(data.list) ? data.list : []
        if (data.max) this.max = data.max
      } catch (e) {
        this.keys = []
        showErrorToast(this.pickErr(e, '加载密钥失败'))
      } finally {
        this.loadingList = false
      }
    },
    async onCreate() {
      this.loading = true
      try {
        const res = await createApiKey(this.name)
        const data = pickApiPayload(res)
        if (!data || !data.key) {
          showErrorToast((res && (res.message || res.error)) || '创建失败')
          return
        }
        this.name = ''
        this.freshSecret = {
          key: data.key,
          secret: data.secret
        }
        showSuccessToast('密钥已创建')
        this.setFlash('已写入主站用户表，请妥善保存 Secret')
        await this.reload()
      } catch (e) {
        showErrorToast(this.pickErr(e, '创建失败'))
        this.setFlash(this.pickErr(e, '创建失败'), true)
      } finally {
        this.loading = false
      }
    },
    async onRevoke(id) {
      if (!confirm('确定吊销该密钥？吊销后不可再用于请求。')) return
      this.loading = true
      try {
        await revokeApiKey(id)
        showSuccessToast('已吊销')
        await this.reload()
      } catch (e) {
        showErrorToast(this.pickErr(e, '吊销失败'))
      } finally {
        this.loading = false
      }
    },
    async onDelete(id) {
      if (!confirm('确定删除该密钥记录？')) return
      this.loading = true
      try {
        await deleteApiKey(id)
        showSuccessToast('已删除')
        await this.reload()
      } catch (e) {
        showErrorToast(this.pickErr(e, '删除失败'))
      } finally {
        this.loading = false
      }
    },
    async copy(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.setFlash('已复制到剪贴板')
      } catch (e) {
        this.setFlash('复制失败，请手动选择', true)
      }
    }
  }
}
</script>

<style scoped>
.create {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  padding: 16px;
  margin-bottom: 16px;
}
.create .field {
  flex: 1;
  min-width: 200px;
}
.create .field label {
  display: block;
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 6px;
}
.create .field input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.25);
  color: var(--text);
}
.flash {
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--accent);
}
.flash.is-err {
  color: var(--danger);
}
.fresh {
  padding: 16px 18px;
  margin-bottom: 16px;
  border: 1px solid rgba(125, 255, 179, 0.35);
}
.fresh__title {
  font-weight: 700;
  margin-bottom: 12px;
  font-size: 14px;
}
.fresh__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 12px;
}
.fresh__row span {
  width: 52px;
  color: var(--muted);
}
.fresh__row code {
  flex: 1;
  min-width: 160px;
  padding: 6px 8px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.35);
  word-break: break-all;
}
.empty {
  padding: 28px;
  text-align: center;
  color: var(--muted);
  font-size: 14px;
}
.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.item {
  padding: 16px 18px;
}
.item__top {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  align-items: flex-start;
}
.item__name {
  font-weight: 700;
  font-size: 15px;
}
.item__meta {
  margin-top: 4px;
  font-size: 11px;
  color: var(--muted);
}
.item__meta .on {
  margin-left: 8px;
  color: var(--accent);
}
.item__meta .off {
  margin-left: 8px;
  color: var(--danger);
}
.item__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.item__actions .btn {
  padding: 7px 12px;
  font-size: 12px;
}
.item__secrets {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--line);
  display: grid;
  gap: 10px;
}
.item__secrets > div {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}
.item__secrets span {
  width: 52px;
  color: var(--muted);
}
.item__secrets code {
  padding: 4px 8px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.35);
  font-size: 12px;
  word-break: break-all;
}
.hint {
  margin-top: 14px;
  font-size: 11px;
  color: var(--muted);
}
</style>
